import { SearchIndexEntry, Sale, SaleChild, DebugLog, SearchOptions as SearchOptions, SearchOptionsStatus } from './types';

/**
 * High-performance hybrid search index for mobile devices
 * Optimized for instant search across large datasets (1000+ sales, 2000+ entries)
 */
export class HybridSearchIndex {
  private salesMap: Map<string, Sale> = new Map();
  private entriesMap: Map<string, SaleChild & { saleId: string; index: number }> = new Map();
  private termIndex: Map<string, Set<string>> = new Map();
  private reverseIndex: Map<string, Set<string>> = new Map();
  private debugCallback?: (log: DebugLog) => void;

  constructor(debugCallback?: (log: DebugLog) => void) {
    this.debugCallback = debugCallback;
  }

  /**
   * Build initial index from sales data
   */
  buildIndex(sales: Sale[]): void {
    const startTime = performance.now();
    this.salesMap.clear();
    this.entriesMap.clear();
    this.termIndex.clear();
    this.reverseIndex.clear();

    let totalTerms = 0;

    sales.forEach(sale => {
      this.salesMap.set(sale.id.toString(), sale);

      // Index sale-level fields
      this.indexSale(sale);

      // Index entries
      sale.details.forEach((entry, index) => {
        const entryId = `${sale.id}-${index}`;
        this.entriesMap.set(entryId, { ...entry, saleId: sale.id.toString(), index });
        this.indexEntry(entryId, entry, sale.id.toString());
      });
    });

    // Count total indexed terms
    this.termIndex.forEach(set => totalTerms += set.size);

    const duration = performance.now() - startTime;
    this.log('info', `Index built: ${sales.length} sales, ${this.entriesMap.size} entries, ${totalTerms} terms (${Math.round(duration)}ms)`);
  }

  /**
   * Incrementally update index when individual sales are updated
   */
  updateSale(sale: Sale): void {
    const startTime = performance.now();
    const saleId = sale.id.toString();

    // Remove old entries
    this.removeSaleFromIndex(saleId);

    // Add updated sale
    this.salesMap.set(saleId, sale);
    this.indexSale(sale);

    // Add updated entries
    sale.details.forEach((entry, index) => {
      const entryId = `${sale.id}-${index}`;
      this.entriesMap.set(entryId, { ...entry, saleId, index });
      this.indexEntry(entryId, entry, saleId);
    });

    const duration = performance.now() - startTime;
    this.log('info', `Sale ${saleId} updated in index (${Math.round(duration)}ms)`);
  }

  /**
   * Batch update multiple sales efficiently
   */
  updateSales(sales: Sale[]): void {
    const startTime = performance.now();

    // Use requestAnimationFrame to avoid blocking UI
    const processBatch = (batchIndex: number) => {
      const batchSize = 50; // Process 50 sales at a time
      const start = batchIndex * batchSize;
      const end = Math.min(start + batchSize, sales.length);

      for (let i = start; i < end; i++) {
        this.updateSale(sales[i]);
      }

      if (end < sales.length) {
        requestAnimationFrame(() => processBatch(batchIndex + 1));
      } else {
        const duration = performance.now() - startTime;
        this.log('info', `Batch update completed: ${sales.length} sales (${Math.round(duration)}ms)`);
      }
    };

    processBatch(0);
  }

  /**
   * Perform fast search across all indexed data
   */
  search(
    query: string,
    options?: SearchOptions,
  ): { total: number, results: SearchIndexEntry[] } {
    const opts = {
      maxResults: 50,
      status: SearchOptionsStatus.ALL,
      ...options,
    };

    if (!query.trim()) return { total: 0, results: [] };

    const startTime = performance.now();
    const searchTerms = this.normalizeSearchText(query).split(' ').filter(Boolean);
    const results: SearchIndexEntry[] = [];
    const seen = new Set<string>();
    const saleResults = new Map<string, SearchIndexEntry>();

    // Search using term index for performance
    searchTerms.forEach(term => {
      const matchingIds = this.termIndex.get(term) || new Set();

      matchingIds.forEach(id => {
        if (seen.has(id)) return;
        seen.add(id);

        if (id.includes('-')) {
          // Entry ID format: "saleId-entryIndex"
          const entry = this.entriesMap.get(id);
          const sale = this.salesMap.get(entry?.saleId || '');

          if (entry && sale) {
            const saleId = entry.saleId;

            switch (opts.status) {
              case SearchOptionsStatus.CHECKED_IN:
                if (!entry.checkedIn) return;
                break;

              case SearchOptionsStatus.NOT_CHECKED_IN:
                if (entry.checkedIn) return;
                break;
            }

            // Check if we already have a sale-level result for this sale
            if (saleResults.has(saleId)) {
              // Update existing sale result to include this entry match
              const existingResult = saleResults.get(saleId)!;
              if (!existingResult.matchedEntries) {
                existingResult.matchedEntries = [];
              }
              existingResult.matchedEntries.push({
                entry,
                matchedField: this.findMatchedField(entry, query),
                searchText: this.getEntrySearchText(entry),
              });
            } else {
              // Create new sale result with this entry
              const saleResult: SearchIndexEntry = {
                id: saleId,
                type: 'sale',
                searchText: this.getSaleSearchText(sale),
                matchedField: this.findMatchedFieldSale(sale, query) || this.findMatchedField(entry, query),
                sale,
                matchedEntries: [{
                  entry,
                  matchedField: this.findMatchedField(entry, query),
                  searchText: this.getEntrySearchText(entry),
                }]
              };
              saleResults.set(saleId, saleResult);
              results.push(saleResult);
            }
          }
        } else {
          // Sale ID
          const sale = this.salesMap.get(id);
          if (sale) {
            switch (opts.status) {
              case SearchOptionsStatus.CHECKED_IN:
                if (!sale.checkedIn) return;
                break;

              case SearchOptionsStatus.NOT_CHECKED_IN:
                if (sale.checkedIn) return;
                break;
            }

            // Check if we already have a result for this sale from entries
            if (saleResults.has(id)) {
              // Update the matched field to prioritize sale-level match
              const existingResult = saleResults.get(id)!;
              const saleMatchedField = this.findMatchedFieldSale(sale, query);
              if (saleMatchedField) {
                existingResult.matchedField = saleMatchedField;
              }
            } else {
              // Create new sale result
              const saleResult: SearchIndexEntry = {
                id,
                type: 'sale',
                searchText: this.getSaleSearchText(sale),
                matchedField: this.findMatchedFieldSale(sale, query),
                sale
              };
              saleResults.set(id, saleResult);
              results.push(saleResult);
            }
          }
        }
      });
    });

    // Sort by relevance (exact matches first, then partial matches)
    results.sort((a, b) => {
      const aRelevance = this.calculateRelevance(a.searchText, query);
      const bRelevance = this.calculateRelevance(b.searchText, query);
      return bRelevance - aRelevance;
    });

    const duration = performance.now() - startTime;
    this.log('info', `Search "${query}": ${results.length} results (${Math.round(duration)}ms)`);

    return {
      total: results.reduce((total, { matchedEntries }) => matchedEntries?.length ? total + matchedEntries.length : total + 1, 0),
      results: results.slice(0, opts.maxResults),
    };
  }

  /**
   * Get current statistics
   */
  getStats() {
    return {
      salesCount: this.salesMap.size,
      entriesCount: this.entriesMap.size,
      termsCount: this.termIndex.size,
      memoryEstimate: this.estimateMemoryUsage()
    };
  }

  private indexSale(sale: Sale): void {
    const saleId = sale.id.toString();
    const searchText = this.getSaleSearchText(sale);
    const terms = this.extractTerms(searchText);

    terms.forEach(term => {
      if (!this.termIndex.has(term)) {
        this.termIndex.set(term, new Set());
      }
      this.termIndex.get(term)!.add(saleId);

      if (!this.reverseIndex.has(saleId)) {
        this.reverseIndex.set(saleId, new Set());
      }
      this.reverseIndex.get(saleId)!.add(term);
    });
  }

  private indexEntry(entryId: string, entry: SaleChild, saleId: string): void {
    const searchText = this.getEntrySearchText(entry);
    const terms = this.extractTerms(searchText);

    terms.forEach(term => {
      if (!this.termIndex.has(term)) {
        this.termIndex.set(term, new Set());
      }
      this.termIndex.get(term)!.add(entryId);

      if (!this.reverseIndex.has(entryId)) {
        this.reverseIndex.set(entryId, new Set());
      }
      this.reverseIndex.get(entryId)!.add(term);
    });
  }

  private removeSaleFromIndex(saleId: string): void {
    // Remove sale
    const saleTerms = this.reverseIndex.get(saleId);
    if (saleTerms) {
      saleTerms.forEach(term => {
        this.termIndex.get(term)?.delete(saleId);
        if (this.termIndex.get(term)?.size === 0) {
          this.termIndex.delete(term);
        }
      });
      this.reverseIndex.delete(saleId);
    }

    // Remove entries
    const entriesToRemove: string[] = [];
    this.entriesMap.forEach((entry, entryId) => {
      if (entry.saleId === saleId) {
        entriesToRemove.push(entryId);
      }
    });

    entriesToRemove.forEach(entryId => {
      const entryTerms = this.reverseIndex.get(entryId);
      if (entryTerms) {
        entryTerms.forEach(term => {
          this.termIndex.get(term)?.delete(entryId);
          if (this.termIndex.get(term)?.size === 0) {
            this.termIndex.delete(term);
          }
        });
        this.reverseIndex.delete(entryId);
      }
      this.entriesMap.delete(entryId);
    });

    this.salesMap.delete(saleId);
  }

  private getSaleSearchText(sale: Sale): string {
    const { customer } = sale;
    return [
      sale.id,
      sale.receiptNumber,
      customer.firstName,
      customer.lastName,
      customer.businessName,
      customer.email,
    ].filter(Boolean).join(' ');
  }

  private getEntrySearchText(entry: SaleChild): string {
    const { customer } = entry;
    return [
      customer.firstName,
      customer.lastName,
      customer.email,
      customer.phoneNumber,
      customer.businessName,
      entry.uniqueNumber,
      entry.ticketName
    ].filter(Boolean).join(' ');
  }

  private extractTerms(text: string): string[] {
    const normalized = this.normalizeSearchText(text);
    const words = normalized.split(/\s+/).filter(Boolean);
    const terms = new Set<string>();

    words.forEach(word => {
      // Add full word
      terms.add(word);

      // Add substrings for partial matching (minimum 2 characters)
      for (let i = 2; i <= word.length; i++) {
        terms.add(word.substring(0, i));
      }
    });

    return Array.from(terms);
  }

  private normalizeSearchText(text: string): string {
    return text.toLowerCase()
      .replace(/[^\w\s@.-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private findMatchedField(entry: SaleChild, query: string): string {
    const queryLower = query.toLowerCase();
    const { customer } = entry;

    if (customer.firstName?.toLowerCase().includes(queryLower)) return 'firstName';
    if (customer.lastName?.toLowerCase().includes(queryLower)) return 'lastName';
    if (customer.email?.toLowerCase().includes(queryLower)) return 'email';
    if (customer.phoneNumber?.toLowerCase().includes(queryLower)) return 'phoneNumber';
    if (customer.businessName?.toLowerCase().includes(queryLower)) return 'businessName';
    if (entry.uniqueNumber?.toLowerCase().includes(queryLower)) return 'uniqueNumber';
    if (entry.ticketName?.toLowerCase().includes(queryLower)) return 'ticketName';

    return 'other';
  }

  private findMatchedFieldSale(sale: Sale, query: string): string {
    const queryLower = query.toLowerCase();
    const { customer } = sale;

    if (sale.id.toString().includes(queryLower)) return 'id';
    if (customer.firstName?.toLowerCase().includes(queryLower)) return 'firstName';
    if (customer.lastName?.toLowerCase().includes(queryLower)) return 'lastName';
    if (customer.email?.toLowerCase().includes(queryLower)) return 'email';
    if (customer.email?.toLowerCase().includes(queryLower)) return 'email';
    if (customer.phoneNumber?.toLowerCase().includes(queryLower)) return 'phoneNumber';
    if (customer.businessName?.toLowerCase().includes(queryLower)) return 'businessName';
    if (sale.receiptNumber?.toLowerCase().includes(queryLower)) return 'receiptNumber';

    return 'other';
  }

  private calculateRelevance(text: string, query: string): number {
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase();

    // Exact match
    if (textLower === queryLower) return 100;

    // Starts with query
    if (textLower.startsWith(queryLower)) return 80;

    // Contains query as whole word
    if (textLower.includes(` ${queryLower} `) || textLower.includes(` ${queryLower}`)) return 60;

    // Contains query as substring
    if (textLower.includes(queryLower)) return 40;

    // Partial word matches
    const queryWords = queryLower.split(' ');
    const textWords = textLower.split(' ');
    let matches = 0;

    queryWords.forEach(queryWord => {
      textWords.forEach(textWord => {
        if (textWord.includes(queryWord)) matches++;
      });
    });

    return matches * 10;
  }

  private estimateMemoryUsage(): string {
    // Rough estimation
    const salesMemory = this.salesMap.size * 1000; // ~1KB per sale
    const entriesMemory = this.entriesMap.size * 500; // ~0.5KB per entry
    const indexMemory = this.termIndex.size * 100; // ~100B per term

    const totalBytes = salesMemory + entriesMemory + indexMemory;
    return totalBytes < 1024 * 1024
      ? `${Math.round(totalBytes / 1024)}KB`
      : `${Math.round(totalBytes / (1024 * 1024))}MB`;
  }

  private log(type: DebugLog['type'], message: string): void {
    if (this.debugCallback) {
      this.debugCallback({
        timestamp: new Date().toLocaleTimeString(),
        message,
        type
      });
    }
  }
}
